import React, { useState } from 'react'
import { Box } from '@mui/material'
import { useParams, useNavigate, useLocation, Outlet, useOutletContext } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@apollo/client'
import { Stack, Typography, Tabs, Tab, Tooltip } from '@mui/material'
import { trackEvent } from 'analytics'

import { OPERATION_FIND_RAN_ROOM } from 'pages/Ran/operations-gql/queries'
import CardActionsButtonsGroup from 'components/global/CardActions/CardActionsButtonsGroup'
import CardActionButton from 'components/global/CardActions/CardActionButton'
import { CodeBox, Device, DeviceOtherInfos, DeviceTopBar } from 'pages/Ran/Card/CardStyles'
import CardPreview, { CardBlockInfo, CardBody, CardHeader } from 'packages/ui/CardPreview'
import { SideContent } from 'components-shared'
import TabPanel from 'components/TabPanel'
import ScrollBox from 'components/global/CUK/ScrollBox'
import { toast } from 'components/global/CUK/Toast/Toast'
import { useUser } from 'store'

import Export from './ActionExport'
import { canEditRans, canSeeStimeFields } from '../permissions'

const Header = ({ data }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const [user] = useUser()
  const { isStime } = useOutletContext()

  return (
    <CardHeader>
      <Stack gap={2} alignItems={'center'}>
        <Typography>{data.room.name}</Typography>
        <Tooltip title={t('ran.card.tooltip_copy_key_wifi')} placement={'top'} arrow>
          <CodeBox
            onClick={() => {
              navigator.clipboard.writeText(data.room.wifi_psk)
              toast.success(t('ran.card.actions.success_copy'))
              trackEvent('private_wifi_passphrase_copied')
            }}>
            <i className={'fa-light fa-copy'} />
            {data.room.wifi_psk}
          </CodeBox>
        </Tooltip>
        <CardActionsButtonsGroup
          style={{
            display: 'flex',
            gap: '16px',
            backgroundColor: 'white'
          }}>
          {canEditRans({ user, isStime }) && (
            <CardActionButton
              iconlabel={'arrow-right-arrow-left'}
              tooltiptitle={t('ran.card.actions.reset')}
              onClick={() => {
                trackEvent('private_wifi_reset', { action_scope: 'item' })
                navigate(`${location.pathname}/reset`)
              }}
              style={{
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                padding: '8px'
              }}
            />
          )}
          <Export room={data.room} />
        </CardActionsButtonsGroup>
      </Stack>
    </CardHeader>
  )
}

const Body = ({ data }) => {
  const { t } = useTranslation()
  const [user] = useUser()
  const [selectedTab, setSelectedTab] = useState('comments')

  const onChangeTab = (event, newValue) => {
    setSelectedTab(newValue)
  }

  return (
    <>
      <Tabs
        value={selectedTab}
        onChange={onChangeTab}
        sx={{
          padding: 0,
          fontWeight: 300
        }}>
        <Tab component="div" label={t('ran.table.description')} value="comments" />
        {Boolean(data.room.devices.length) && (
          <Tab
            component="div"
            label={
              <Stack direction="row" spacing={1} useFlexGap>
                {t('ran.table.users_devices')}
                <Box
                  sx={{
                    padding: '0 0.375rem',
                    backgroundColor: '#f5f5f5',
                    color: '#757575',
                    borderRadius: '0.5rem'
                  }}>
                  {data.room.devices.length}
                </Box>
              </Stack>
            }
            value="users-devices"
          />
        )}
      </Tabs>

      <TabPanel value={selectedTab} index="comments">
        <CardBody>
          <Box display={'flex'} flexDirection={'column'} gap={2}>
            <CardBlockInfo title={t('ran.card.ssid')} subtitle={data.room.ssid_name || '-'} />
            {canSeeStimeFields(user) && <CardBlockInfo title={t('ran.card.vlan')} subtitle={data.room.vlan || '-'} />}
            <CardBlockInfo
              title={t('ran.card.description')}
              subtitle={data.room.description ? data.room.description : '-'}
            />
            <CardBlockInfo
              title={t('ran.card.created_at')}
              subtitle={new Date(data.room.created_at).toLocaleDateString()}
            />
            <CardBlockInfo
              title={t('ran.card.created_by')}
              subtitle={data.room.user ? `${data.room.user.first_name} ${data.room.user.last_name}` : '-'}
            />
          </Box>
        </CardBody>
      </TabPanel>

      <TabPanel value={selectedTab} index="users-devices">
        <ScrollBox
          setScrollTo={null}
          scrollTo={null}
          setScrollTop={null}
          fetchNextPage={null}
          tolerance={undefined}
          maxHeight={null}
          shouldFetchNextPage={false}>
          {data.room.devices.map(device => {
            return (
              <Device key={device.mac}>
                <DeviceTopBar>
                  <CardBlockInfo title={t('ran.card.device')} subtitle={device.mac} />
                </DeviceTopBar>
                <DeviceOtherInfos>
                  <CardBlockInfo
                    title={t('ran.card.connected_since')}
                    subtitle={new Date(device.connected_since).toLocaleDateString()}
                  />
                  <CardBlockInfo
                    title={<i className={'fa-light fa-cloud-arrow-down'} />}
                    subtitle={device.data_downloaded || '-'}
                  />
                  <CardBlockInfo
                    title={<i className={'fa-light fa-cloud-arrow-up'} />}
                    subtitle={device.data_uploaded || '-'}
                  />
                </DeviceOtherInfos>
              </Device>
            )
          })}
        </ScrollBox>
      </TabPanel>
    </>
  )
}

const Error = ({ error }) => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const outletContext = useOutletContext()
  return (
    <SideContent style={{ maxWidth: '440px', marginTop: 0 }}>
      <CardPreview title={t('ui_elements.card_title')} onClose={() => navigate({ pathname: '..' })}>
        <Typography color={'error'} sx={{ fontWeight: 600, fontSize: 14 }}>
          {t('actions.error')} :
        </Typography>
        <Typography color={'error'} sx={{ fontSize: 13 }}>
          {error.graphQLErrors ? error.graphQLErrors[0].message : error.message}
        </Typography>
      </CardPreview>
      <Outlet context={outletContext} />
    </SideContent>
  )
}
const Card = () => {
  const { t } = useTranslation()
  const { roomId, siteId } = useParams()
  const [user] = useUser()
  const { isStime } = useOutletContext()
  const navigate = useNavigate()
  const outletContext = useOutletContext()
  const { data, loading, error } = useQuery(OPERATION_FIND_RAN_ROOM, {
    variables: { sf_id: siteId, room_id: parseInt(roomId) }
  })

  if (error) {
    return <Error error={error} />
  }
  return (
    <SideContent style={{ maxWidth: '440px', marginTop: 0 }}>
      <CardPreview
        title={t('ui_elements.card_title')}
        onClose={() => navigate({ pathname: '..' })}
        components={
          data && canEditRans({ user, isStime })
            ? {
                ActionButtons: () => (
                  <CardActionsButtonsGroup>
                    <CardActionButton
                      iconlabel={'pen'}
                      tooltiptitle={t('ran.card.actions.edit')}
                      onClick={() => {
                        trackEvent('private_wifi_update', { action_scope: 'item' })
                        navigate(`${location.pathname}/edit`)
                      }}
                    />
                    <CardActionButton
                      iconlabel={'trash'}
                      tooltiptitle={t('ran.card.actions.delete')}
                      onClick={() => {
                        trackEvent('private_wifi_delete', { action_scope: 'item' })
                        navigate(`${location.pathname}/delete`)
                      }}
                    />
                  </CardActionsButtonsGroup>
                )
              }
            : {}
        }>
        {loading ? (
          <Stack justifyContent="center" spacing={2}>
            <Typography sx={{ fontSize: 13 }}>{t('ui_elements.loading')}</Typography>
          </Stack>
        ) : (
          <>
            <Header data={data} />
            <Body data={data} />
          </>
        )}
      </CardPreview>
      <Outlet context={outletContext} />
    </SideContent>
  )
}

export default Card
